import { nextTick, App, DirectiveBinding } from 'vue'

interface DragWindowOptions {
  resizable?: boolean
  minWidth?: number
  minHeight?: number
}

type DragWindowElement = HTMLElement & {
  __dragWindowCleanup?: () => void
}

const DEFAULT_MIN_WIDTH = 320
const DEFAULT_MIN_HEIGHT = 220
const INTERACTIVE_DRAG_TARGET_SELECTOR = [
  '.drag-ignore',
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  '[role="button"]',
  '[contenteditable="true"]',
].join(',')

export default function useDragWindowDirective (app: App): void {
  app.directive('drag-window', {
    async mounted (el: DragWindowElement, binding: DirectiveBinding<DragWindowOptions | undefined>) {
      await nextTick()

      const modal = el
      const header = el.getElementsByClassName('drag-title')[0] as HTMLElement | undefined
      if (!header) {
        return
      }

      const options = binding.value ?? {}
      const minWidth = options.minWidth ?? DEFAULT_MIN_WIDTH
      const minHeight = options.minHeight ?? DEFAULT_MIN_HEIGHT
      const cleanupFns: Array<() => void> = []

      header.style.cursor = 'move'
      header.style.touchAction = 'none'

      const onDragStart = (event: PointerEvent) => {
        if (isDragResizeTarget(event.target) || isInteractiveDragTarget(event.target)) {
          return
        }

        const startX = event.clientX
        const startY = event.clientY
        const startLeft = modal.offsetLeft
        const startTop = modal.offsetTop

        header.setPointerCapture?.(event.pointerId)
        event.preventDefault()

        const onDragMove = (moveEvent: PointerEvent) => {
          setWindowPosition(
            modal,
            startLeft + moveEvent.clientX - startX,
            startTop + moveEvent.clientY - startY
          )
        }

        const onDragEnd = () => {
          window.removeEventListener('pointermove', onDragMove)
          window.removeEventListener('pointerup', onDragEnd)
          header.releasePointerCapture?.(event.pointerId)
        }

        window.addEventListener('pointermove', onDragMove)
        window.addEventListener('pointerup', onDragEnd)
      }

      header.addEventListener('pointerdown', onDragStart)
      cleanupFns.push(() => header.removeEventListener('pointerdown', onDragStart))

      if (options.resizable) {
        const resizeHandle = ensureResizeHandle(modal)
        resizeHandle.style.touchAction = 'none'

        const onResizeStart = (event: PointerEvent) => {
          const startX = event.clientX
          const startY = event.clientY
          const startWidth = modal.offsetWidth
          const startHeight = modal.offsetHeight

          resizeHandle.setPointerCapture?.(event.pointerId)
          event.preventDefault()
          event.stopPropagation()

          const onResizeMove = (moveEvent: PointerEvent) => {
            setWindowSize(
              modal,
              startWidth + moveEvent.clientX - startX,
              startHeight + moveEvent.clientY - startY,
              minWidth,
              minHeight
            )
          }

          const onResizeEnd = () => {
            window.removeEventListener('pointermove', onResizeMove)
            window.removeEventListener('pointerup', onResizeEnd)
            resizeHandle.releasePointerCapture?.(event.pointerId)
          }

          window.addEventListener('pointermove', onResizeMove)
          window.addEventListener('pointerup', onResizeEnd)
        }

        resizeHandle.addEventListener('pointerdown', onResizeStart)
        cleanupFns.push(() => resizeHandle.removeEventListener('pointerdown', onResizeStart))
      }

      modal.__dragWindowCleanup = () => {
        cleanupFns.forEach(cleanup => cleanup())
      }
    },

    beforeUnmount (el: DragWindowElement) {
      el.__dragWindowCleanup?.()
      delete el.__dragWindowCleanup
    }
  })
}

function ensureResizeHandle (modal: HTMLElement) {
  const existingHandle = modal.querySelector('.drag-resize-handle') as HTMLElement | null
  if (existingHandle) {
    return existingHandle
  }

  const resizeHandle = document.createElement('div')
  resizeHandle.className = 'drag-resize-handle'
  modal.appendChild(resizeHandle)
  return resizeHandle
}

function setWindowPosition (modal: HTMLElement, left: number, top: number) {
  const parent = getBoundaryParent(modal)
  const maxLeft = Math.max(0, parent.clientWidth - modal.offsetWidth)
  const maxTop = Math.max(0, parent.clientHeight - modal.offsetHeight)

  modal.style.marginLeft = '0'
  modal.style.left = clamp(left, 0, maxLeft) + 'px'
  modal.style.top = clamp(top, 0, maxTop) + 'px'
}

function setWindowSize (modal: HTMLElement, width: number, height: number, minWidth: number, minHeight: number) {
  const parent = getBoundaryParent(modal)
  const maxWidth = Math.max(minWidth, parent.clientWidth - modal.offsetLeft)
  const maxHeight = Math.max(minHeight, parent.clientHeight - modal.offsetTop)

  modal.style.width = clamp(width, minWidth, maxWidth) + 'px'
  modal.style.height = clamp(height, minHeight, maxHeight) + 'px'
}

function getBoundaryParent (modal: HTMLElement) {
  return (modal.offsetParent as HTMLElement | null) ?? modal.parentElement ?? document.documentElement
}

function clamp (value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function isDragResizeTarget (target: EventTarget | null) {
  return target instanceof Element && !!target.closest('.drag-resize-handle')
}

function isInteractiveDragTarget (target: EventTarget | null) {
  return target instanceof Element && !!target.closest(INTERACTIVE_DRAG_TARGET_SELECTOR)
}
